import { Controller } from "@hotwired/stimulus"
import { toast } from "../utils/toast"

export default class extends Controller {
  static targets = ["video", "canvas", "captureButton", "thumbnailContainer", "modal", "modalImage", "saveButton", "successMessage", "thumbnailTemplate", "badgeTemplate", "flashTemplate"]

  connect() {
    this.capturedPhotos = []
    this.stream = null
    this.initializeCamera()
  }

  disconnect() {
    this.stopCamera()
  }

  async initializeCamera() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment", // Prefer rear camera
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      })

      this.videoTarget.srcObject = this.stream
      this.videoTarget.play()

      // Show capture button once camera is ready
      this.videoTarget.addEventListener('loadedmetadata', () => {
        this.captureButtonTarget.classList.remove('hidden')
      })
    } catch (error) {
      console.error('Error accessing camera:', error)
      let errorMessage = 'Unable to access camera. '

      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please allow camera access and try again.'
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No camera found.'
      } else if (error.name === 'NotSupportedError') {
        errorMessage += 'Camera is not supported in this browser.'
      } else {
        errorMessage += 'Please check your permissions and try again.'
      }

      toast.error(errorMessage)
    }
  }

  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
  }

  capturePhoto() {
    // Set canvas size to match video
    this.canvasTarget.width = this.videoTarget.videoWidth
    this.canvasTarget.height = this.videoTarget.videoHeight

    // Draw current video frame to canvas
    const context = this.canvasTarget.getContext('2d')
    context.drawImage(this.videoTarget, 0, 0)

    // Get the data URL immediately after drawing
    const dataUrl = this.canvasTarget.toDataURL('image/jpeg', 0.9)

    // Convert canvas to blob and store
    this.canvasTarget.toBlob((blob) => {
      const photoData = {
        blob: blob,
        dataUrl: dataUrl,
        timestamp: Date.now()
      }

      this.capturedPhotos.push(photoData)
      this.updateThumbnails()
      this.updateSaveButtonVisibility()

      // Visual feedback
      this.flashScreen()
    }, 'image/jpeg', 0.9)
  }

  flashScreen() {
    // Clone the flash template
    const flash = this.flashTemplateTarget.content.firstElementChild.cloneNode(true)
    document.body.appendChild(flash)

    setTimeout(() => {
      flash.classList.add('transition-opacity', 'duration-300', 'opacity-0')
      setTimeout(() => flash.remove(), 300)
    }, 100)
  }

  updateThumbnails() {
    console.log('Updating thumbnails, photos:', this.capturedPhotos.length)
    this.thumbnailContainerTarget.innerHTML = ''

    if (this.capturedPhotos.length === 0) return

    // Set container width for 2 stacked thumbnails (80px + 12px offset = 92px)
    this.thumbnailContainerTarget.classList.add('w-[92px]')

    // Show only the last 2 photos (most recent on top)
    const photosToShow = this.capturedPhotos.slice(-2).reverse()

    photosToShow.forEach((photo, displayIndex) => {
      const actualIndex = this.capturedPhotos.length - 1 - displayIndex
      console.log(`Creating thumbnail ${actualIndex}, dataUrl length:`, photo.dataUrl?.length)

      // Clone the thumbnail template
      const thumbnail = this.thumbnailTemplateTarget.content.firstElementChild.cloneNode(true)

      // Set positioning for stacked thumbnails
      // Bottom photo (displayIndex 1) at left 0, top photo (displayIndex 0) at left 12px (left-3)
      thumbnail.classList.add(displayIndex === 0 ? 'left-3' : 'left-0', 'bottom-0')
      thumbnail.style.zIndex = 10 - displayIndex // Top photo has higher z-index
      thumbnail.onclick = () => this.viewPhoto({ params: { index: actualIndex } })

      // Set the image source and alt text
      const img = thumbnail.querySelector('[data-camera-capture-target="thumbnailImage"]')
      img.src = photo.dataUrl
      img.alt = `Photo ${actualIndex + 1}`
      img.onerror = () => console.error('Failed to load image', actualIndex)
      img.onload = () => console.log('Image loaded successfully', actualIndex)

      // Add count badge to the top (most recent) photo
      if (displayIndex === 0 && this.capturedPhotos.length > 1) {
        const badge = this.badgeTemplateTarget.content.firstElementChild.cloneNode(true)
        badge.textContent = this.capturedPhotos.length
        thumbnail.appendChild(badge)
        console.log('Added badge with count:', this.capturedPhotos.length)
      }

      this.thumbnailContainerTarget.appendChild(thumbnail)
      console.log('Thumbnail appended to container', actualIndex)
    })
  }

  updateSaveButtonVisibility() {
    if (this.capturedPhotos.length > 0) {
      this.saveButtonTarget.classList.remove('hidden')
    } else {
      this.saveButtonTarget.classList.add('hidden')
    }
  }

  viewPhoto(event) {
    const index = parseInt(event.params.index)
    this.currentPhotoIndex = index
    this.modalImageTarget.src = this.capturedPhotos[index].dataUrl
    this.modalTarget.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
  }

  closeModal() {
    this.modalTarget.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
  }

  nextPhoto() {
    if (this.capturedPhotos.length > 0) {
      this.currentPhotoIndex = (this.currentPhotoIndex + 1) % this.capturedPhotos.length
      this.modalImageTarget.src = this.capturedPhotos[this.currentPhotoIndex].dataUrl
    }
  }

  previousPhoto() {
    if (this.capturedPhotos.length > 0) {
      this.currentPhotoIndex = this.currentPhotoIndex === 0
        ? this.capturedPhotos.length - 1
        : this.currentPhotoIndex - 1
      this.modalImageTarget.src = this.capturedPhotos[this.currentPhotoIndex].dataUrl
    }
  }

  deleteCurrentPhoto() {
    if (this.currentPhotoIndex !== undefined) {
      this.capturedPhotos.splice(this.currentPhotoIndex, 1)
      this.updateThumbnails()
      this.updateSaveButtonVisibility()
      this.closeModal()
    }
  }

  showSuccessMessage() {
    this.successMessageTarget.classList.remove('hidden')

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.successMessageTarget.classList.add('hidden')
    }, 5000)
  }

  close() {
    // Go back to the previous page or project
    window.history.back()
  }

  getAccumulatedPhotos() {
    return this.capturedPhotos
  }
}
